import { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { formatApiError, waLink, inr, fmtDate } from "@/lib/utils";
import {
  X, FileText, Download, MessageCircle, Loader2, ScrollText,
  Plus, Trash2, ExternalLink, Copy, Check, Mail, Send,
  CheckCircle2, Sparkles, Gift, CopyPlus, Eye, Smartphone, Clock,
  XCircle, AlertTriangle, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import ProposalContentEditor from "@/components/ProposalContentEditor";
import { useCapabilities } from "@/context/PermissionsContext";

// iter144 — Lifecycle status pill colors (mirrored on the Bookings row)
const STATUS_STYLES = {
  draft:    { label: "Draft",    cls: "bg-stone-100 text-stone-700 border-stone-200" },
  sent:     { label: "Sent",     cls: "bg-sky-50 text-sky-700 border-sky-200" },
  viewed:   { label: "Viewed",   cls: "bg-amber-50 text-amber-800 border-amber-200" },
  accepted: { label: "Accepted", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rejected: { label: "Declined", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  expired:  { label: "Expired",  cls: "bg-stone-100 text-stone-500 border-stone-200" },
};

/**
 * iter128 — Proposal Modal (evolved from the iter62 Quotation Modal).
 *
 * Two-step flow:
 *   Step 1: line-item editor pre-filled from booking.service_flags. Admin
 *           can override rates, add/remove custom lines, apply GST, set
 *           advance-expected, add a client email + free-form notes.
 *   Step 2: Proposal summary + share tools:
 *              • Beautiful proposal page link ( /proposal/{token} )
 *              • Printable PDF preview + download
 *              • WhatsApp share (pre-filled multi-line message)
 *              • Email share (Resend-powered)
 *              • Live "Accepted" badge when the client soft-accepts on
 *                the public page.
 *
 * Backend endpoint stays `/bookings/{id}/quote` for compatibility; the
 * data model on the booking gets a `status` (sent/accepted) + `client_email`.
 */
export default function ProposalModal({ open, booking, onClose }) {
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [lines, setLines] = useState([]);
  const [gstPercent, setGstPercent] = useState("");
  const [advanceExpected, setAdvanceExpected] = useState("");
  const [validity, setValidity] = useState(15);
  const [notes, setNotes] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState({ pdf: false, page: false });
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");
  const [acceptedInfo, setAcceptedInfo] = useState(null);
  // iter145 — Snapshot of the initially-loaded proposal state. When the
  // booking already has a quote we compare current form fields against
  // this snapshot to compute `isDirty` so the Update button stays
  // disabled until the studio actually changes something.
  const [snapshot, setSnapshot] = useState(null);
  // iter146 — Editable per-event subtotals keyed by event_date
  // ("" = the "Package Inclusions" group). Empty string / null means
  // "no override — use the auto sum". Persisted on the quote so it
  // survives reopens.
  const [subtotalOverrides, setSubtotalOverrides] = useState({});
  // iter153 — Proposal-scoped extra events. Studios can add a new
  // function (Haldi / Mehendi / Reception …) directly inside the
  // Proposal Editor without recreating the booking. Each entry:
  // { event_date, event_name }. Persisted on the quote (`extra_events`)
  // so re-opening restores them.
  const [extraEvents, setExtraEvents] = useState([]);
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [newEventName, setNewEventName] = useState("");
  const [newEventDate, setNewEventDate] = useState("");
  // iter176 — Admin Content Editor: per-proposal customization drawer.
  const [contentEditorOpen, setContentEditorOpen] = useState(false);
  const proposalCaps = useCapabilities("proposal");

  const hasExistingQuote = !!booking?.quote?.token;

  const bookingDates = useMemo(() => {
    if (!booking) return [];
    const raw = [
      booking.day1, booking.day2, booking.day3, booking.day4, booking.day5,
      ...(Array.isArray(booking.extra_days) ? booking.extra_days.map((d) => d?.date) : []),
      // iter153 — Extra events added inside the Proposal Editor also
      // contribute to the date dropdown so their line items can bind.
      ...extraEvents.map((ev) => ev?.event_date),
    ];
    return Array.from(new Set(raw.map((d) => (d || "").trim()).filter(Boolean))).sort();
  }, [booking, extraEvents]);
  const defaultDate = bookingDates[0] || "";

  useEffect(() => {
    if (!open || !booking) return;
    setStep(1);
    setResult(null);
    setErr("");
    setAcceptedInfo(null);
    setEmailMessage("");
    // Pre-fill client email from any existing proposal on the booking, else
    // keep the field blank so the user can type it in.
    setClientEmail(booking.quote?.client_email || "");
    // iter144 — If a proposal already exists on this booking, restore its
    // full state so the editor is a proper "edit" surface, not a
    // regeneration wizard that silently discards data.
    const existingQuote = booking.quote || {};
    const hasExisting = Array.isArray(existingQuote.line_items) && existingQuote.line_items.length > 0;
    if (hasExisting) {
      const restoredLines = existingQuote.line_items.map((li) => ({
        label: li.label || "",
        quantity: Number(li.quantity) || 1,
        rate: Number(li.rate) || 0,
        event_date: li.event_date || "",
        is_complimentary: !!li.is_complimentary,
      }));
      setLines(restoredLines);
      const gstStr = existingQuote.gst_percent ? String(existingQuote.gst_percent) : "";
      const advStr = existingQuote.advance_expected ? String(existingQuote.advance_expected) : "";
      const validityVal = existingQuote.validity_days || 15;
      const notesStr = existingQuote.notes || "";
      setGstPercent(gstStr);
      setAdvanceExpected(advStr);
      setValidity(validityVal);
      setNotes(notesStr);
      // iter146 — Restore any saved per-event overrides so studios can
      // continue tweaking a subtotal they already customised.
      const restoredOverrides = {};
      const rawOv = existingQuote.event_subtotal_overrides || {};
      Object.entries(rawOv).forEach(([k, v]) => {
        restoredOverrides[k] = String(v);
      });
      setSubtotalOverrides(restoredOverrides);
      // iter153 — Restore proposal-scoped extra events.
      const restoredExtras = Array.isArray(existingQuote.extra_events)
        ? existingQuote.extra_events
            .map((ev) => ({
              event_date: String(ev?.event_date || "").trim(),
              event_name: String(ev?.event_name || "").trim(),
            }))
            .filter((ev) => ev.event_date)
        : [];
      setExtraEvents(restoredExtras);
      setAddEventOpen(false);
      setNewEventName("");
      setNewEventDate("");
      // iter145 — Snapshot the exact values we just loaded so `isDirty`
      // can tell whether anything actually changed.
      setSnapshot({
        lines: restoredLines,
        gstPercent: gstStr,
        advanceExpected: advStr,
        validity: validityVal,
        notes: notesStr,
        clientEmail: existingQuote.client_email || "",
        subtotalOverrides: { ...restoredOverrides },
        extraEvents: restoredExtras.map((e) => ({ ...e })),
      });
    } else {
      setGstPercent("");
      setAdvanceExpected("");
      setValidity(15);
      setNotes(booking.inquiry_notes || booking.last_updates || "");
      setSubtotalOverrides({});
      setExtraEvents([]);
      setAddEventOpen(false);
      setNewEventName("");
      setNewEventDate("");
      setSnapshot(null);
    }
    (async () => {
      try {
        const { data } = await api.get("/public/services");
        const catalog = data.services || [];
        setServices(catalog);
        if (hasExisting) return; // don't clobber restored lines with seeded ones
        const flags = Array.isArray(booking.service_flags) ? booking.service_flags : [];
        const seedKeys = flags.length ? flags : ["candid_photo", "cinematography"];
        const dates = [
          booking.day1, booking.day2, booking.day3, booking.day4, booking.day5,
          ...(Array.isArray(booking.extra_days) ? booking.extra_days.map((d) => d?.date) : []),
        ].map((d) => (d || "").trim()).filter(Boolean);
        // iter135 — Multi-day bookings must get one Function card per event
        // date. We fan the seed services out across EVERY date so the admin
        // sees the correct number of line items pre-filled and can tweak
        // amounts per day without duplicating rows manually.
        const dateSpread = dates.length ? dates : [""];
        const catalogPicks = seedKeys
          .map((k) => catalog.find((c) => c.key === k))
          .filter(Boolean);
        const seededLines = [];
        dateSpread.forEach((d) => {
          catalogPicks.forEach((c) => {
            seededLines.push({ label: c.label, quantity: 1, rate: c.rate, event_date: d, is_complimentary: false });
          });
        });
        setLines(seededLines.length ? seededLines : [{ label: "", quantity: 1, rate: 0, event_date: dateSpread[0] || "", is_complimentary: false }]);
      } catch {
        setServices([]);
        if (!hasExisting) {
          setLines([{ label: "", quantity: 1, rate: 0, event_date: "", is_complimentary: false }]);
        }
      }
    })();
  }, [open, booking]);

  // iter146 — Per-event auto subtotals (excluding complimentary lines).
  // Keys use the event_date value from each line ("" = Package Inclusions).
  const eventAutoSubtotals = useMemo(() => {
    const out = {};
    lines.forEach((li) => {
      if (li.is_complimentary) return;
      const key = (li.event_date || "").trim();
      out[key] = (out[key] || 0) + Number(li.rate || 0) * Number(li.quantity || 1);
    });
    return out;
  }, [lines]);
  // Effective subtotal per event = override (when set) or auto sum.
  const effectiveEventSubtotal = (key) => {
    const raw = subtotalOverrides?.[key];
    if (raw === undefined || raw === null || String(raw).trim() === "") {
      return Number(eventAutoSubtotals[key] || 0);
    }
    const n = Number(raw);
    return isFinite(n) && n >= 0 ? n : Number(eventAutoSubtotals[key] || 0);
  };
  const subtotal = useMemo(() => {
    let s = 0;
    Object.keys(eventAutoSubtotals).forEach((k) => {
      s += effectiveEventSubtotal(k);
    });
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventAutoSubtotals, subtotalOverrides]);
  const gstAmt = useMemo(
    () => +(subtotal * Number(gstPercent || 0) / 100).toFixed(2),
    [subtotal, gstPercent]
  );
  const total = subtotal + gstAmt;
  const balance = +(total - Number(advanceExpected || 0)).toFixed(2);

  const [analytics, setAnalytics] = useState(null);
  const [publicStatus, setPublicStatus] = useState("sent");

  // Poll the public proposal endpoint while step 2 is open so we can
  // surface fresh analytics (view count, last viewed device/time) and
  // catch soft-acceptance without a page refresh.
  // (Declared BEFORE the early-return so hook order stays stable.)
  useEffect(() => {
    if (!result?.token || step !== 2) return;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/public/proposals/${result.token}`);
        if (stop) return;
        setAnalytics(data.analytics || null);
        setPublicStatus(data.status || "sent");
        if (data.status === "accepted") {
          setAcceptedInfo({ at: data.accepted_at });
        }
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => { stop = true; clearInterval(id); };
  }, [result?.token, step]);

  // iter147 — Escape-to-close for the drawer. Trap it once at document
  // level so the handler doesn't fight with individual inputs' own
  // Escape behaviour (e.g., select popovers).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // iter145 — Compute whether the editor has any unsaved changes vs the
  // snapshot we loaded from the server. The snapshot only exists when
  // this booking already has a proposal — for brand-new proposals the
  // button is always enabled (there's nothing to compare against).
  // Declared BEFORE the early-return so hook order stays stable.
  const isDirty = useMemo(() => {
    if (!snapshot) return true;
    const currentClientEmail = (clientEmail || "").trim();
    const snapClientEmail = (snapshot.clientEmail || "").trim();
    if (currentClientEmail !== snapClientEmail) return true;
    if ((gstPercent || "") !== (snapshot.gstPercent || "")) return true;
    if ((advanceExpected || "") !== (snapshot.advanceExpected || "")) return true;
    if (Number(validity || 0) !== Number(snapshot.validity || 0)) return true;
    if ((notes || "") !== (snapshot.notes || "")) return true;
    if (lines.length !== snapshot.lines.length) return true;
    for (let i = 0; i < lines.length; i++) {
      const a = lines[i]; const b = snapshot.lines[i];
      if ((a.label || "") !== (b.label || "")) return true;
      if (Number(a.quantity || 1) !== Number(b.quantity || 1)) return true;
      if (Number(a.rate || 0) !== Number(b.rate || 0)) return true;
      if ((a.event_date || "") !== (b.event_date || "")) return true;
      if (!!a.is_complimentary !== !!b.is_complimentary) return true;
    }
    // iter146 — Override changes count as dirty too.
    const snapOv = snapshot.subtotalOverrides || {};
    const keys = new Set([...Object.keys(snapOv), ...Object.keys(subtotalOverrides || {})]);
    for (const k of keys) {
      const av = String((subtotalOverrides || {})[k] ?? "").trim();
      const bv = String(snapOv[k] ?? "").trim();
      if (av !== bv) return true;
    }
    // iter153 — Extra events added inside the editor count as dirty.
    const snapExtras = snapshot.extraEvents || [];
    if (extraEvents.length !== snapExtras.length) return true;
    for (let i = 0; i < extraEvents.length; i++) {
      const a = extraEvents[i]; const b = snapExtras[i] || {};
      if ((a.event_date || "") !== (b.event_date || "")) return true;
      if ((a.event_name || "") !== (b.event_name || "")) return true;
    }
    return false;
  }, [snapshot, lines, gstPercent, advanceExpected, validity, notes, clientEmail, subtotalOverrides, extraEvents]);

  if (!open || !booking) return null;

  const addLine = () => setLines((ls) => [...ls, { label: "", quantity: 1, rate: 0, event_date: defaultDate, is_complimentary: false }]);
  const removeLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));
  const duplicateLine = (i) => setLines((ls) => {
    const src = ls[i];
    if (!src) return ls;
    const copy = { ...src };
    return [...ls.slice(0, i + 1), copy, ...ls.slice(i + 1)];
  });
  const patchLine = (i, patch) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addFromCatalog = (svc) => {
    if (lines.some((l) => l.label === svc.label)) return;
    setLines((ls) => [...ls, { label: svc.label, quantity: 1, rate: svc.rate, event_date: defaultDate, is_complimentary: false }]);
  };

  const generate = async (e) => {
    e?.preventDefault?.();
    setErr("");
    const cleanLines = lines
      .map((l) => ({
        label: (l.label || "").trim(),
        quantity: Math.max(1, Number(l.quantity || 1)),
        rate: Number(l.rate || 0),
        event_date: (l.event_date || "").trim(),
        is_complimentary: !!l.is_complimentary,
      }))
      .filter((l) => l.label && l.rate >= 0);
    if (!cleanLines.length) {
      setErr("Add at least one line item.");
      return;
    }
    // iter144/145 — Confirm before updating an already-Accepted proposal.
    // Update keeps the same public link + acceptance state; only line
    // items and totals change. Still worth a heads-up.
    const existingAccepted = (booking?.quote?.status || "").toLowerCase() === "accepted";
    if (existingAccepted) {
      const ok = typeof window !== "undefined"
        ? window.confirm(
            "This proposal was already Accepted by the client. Updating will change the line items on the same public link but keep the acceptance mark. Continue?"
          )
        : true;
      if (!ok) return;
    }
    setBusy(true);
    try {
      const payload = {
        client_name: booking.client_name || booking.couple_name || "Client",
        client_contact: booking.contact || "",
        client_email: (clientEmail || "").trim(),
        couple_name: booking.couple_name || "",
        event: booking.event || "Wedding",
        event_date: booking.day1 || "",
        location: booking.location || "",
        line_items: cleanLines,
        gst_percent: Number(gstPercent || 0),
        advance_expected: Number(advanceExpected || 0),
        notes,
        validity_days: Number(validity || 0),
        // iter146 — Per-event subtotal overrides (only send keys with a
        // parseable, non-empty numeric value; the backend treats missing
        // keys as "use the auto sum").
        event_subtotal_overrides: Object.fromEntries(
          Object.entries(subtotalOverrides || {}).flatMap(([k, v]) => {
            const raw = String(v ?? "").trim();
            if (!raw) return [];
            const n = Number(raw);
            if (!isFinite(n) || n < 0) return [];
            return [[k, n]];
          })
        ),
        // iter153 — Proposal-scoped extra events (Haldi / Mehendi /
        // Reception …) added inside the editor. Purely additive; the
        // source booking is left untouched.
        extra_events: extraEvents
          .map((ev) => ({
            event_date: (ev.event_date || "").trim(),
            event_name: (ev.event_name || "").trim(),
          }))
          .filter((ev) => ev.event_date),
      };
      const { data } = await api.post(`/bookings/${booking.id}/quote`, payload);
      setResult(data);
      setStep(2);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const fullPdfUrl = result ? `${API}${result.pdf_url}` : "";
  // Public proposal HTML page (mobile-first landing) — served by the
  // frontend at /proposal/{token}. Origin comes from window (works in
  // preview and prod).
  const proposalOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const proposalPageUrl = result ? `${proposalOrigin}/proposal/${result.token}` : "";
  const waMessage = result
    ? (result.wa_message_template || "")
        .replace("{{PDF_URL}}", fullPdfUrl)
        .replace("{{PROPOSAL_URL}}", proposalPageUrl)
    : "";
  const waHref = booking.contact ? waLink(booking.contact, waMessage) : null;

  const copyLink = async (kind) => {
    const url = kind === "page" ? proposalPageUrl : fullPdfUrl;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied((c) => ({ ...c, [kind]: true }));
      toast.success("Link copied");
      setTimeout(() => setCopied((c) => ({ ...c, [kind]: false })), 1800);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const sendEmail = async () => {
    if (!clientEmail?.trim()) {
      toast.error("Enter the client's email first");
      return;
    }
    setEmailBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/proposal/email`, {
        recipient_email: clientEmail.trim(),
        message: emailMessage,
      });
      toast.success(`Proposal sent to ${clientEmail}`);
    } catch (e2) {
      const detail = formatApiError(e2.response?.data?.detail) || e2.message;
      toast.error(detail);
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40 kb-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={hasExistingQuote ? "Update Proposal" : "Create Proposal"}
    >
      <div
        className="bg-white w-full max-w-3xl h-full flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="proposal-modal"
      >
        <div className="flex items-center justify-between px-6 h-16 border-b border-stone-200 shrink-0">
          <div className="flex items-center gap-3">
            <ScrollText className="w-5 h-5 text-rose-700" strokeWidth={1.6} />
            <div>
              <h2 className="font-heading text-lg font-semibold leading-tight">
                {step === 1
                  ? (hasExistingQuote ? "Update Proposal" : "Create Proposal")
                  : "Proposal ready"}
              </h2>
              <div className="text-[11px] uppercase tracking-wider text-stone-500">
                {booking.couple_name || booking.client_name}
                {booking.day1 ? ` · ${fmtDate(booking.day1)}` : ""}
              </div>
            </div>
          </div>
          <button
            className="btn-ghost p-2 rounded kb-tap"
            onClick={onClose}
            data-testid="proposal-close"
            aria-label="Close proposal editor"
          >
            <X className="w-5 h-5" strokeWidth={1.6} aria-hidden="true" />
          </button>
        </div>

        {step === 1 && (
          <form onSubmit={generate} className="flex-1 overflow-y-auto p-6 space-y-6">
            <div className="space-y-3">
              <div className="flex items-end justify-between">
                <div>
                  <div className="editorial-eyebrow">Line Items</div>
                  <p className="text-xs text-stone-500 mt-1">
                    Pre-filled from client's selected services. Add / remove / tweak rates freely.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setAddEventOpen((v) => !v);
                      setNewEventName("");
                      setNewEventDate("");
                    }}
                    className="btn btn-outline btn-sm"
                    data-testid="proposal-add-event"
                    title="Add another event / day (Haldi, Mehendi, Reception…)"
                  >
                    <Plus className="w-3.5 h-3.5" /> Event / Day
                  </button>
                  <button type="button" onClick={addLine} className="btn btn-outline btn-sm" data-testid="proposal-add-line">
                    <Plus className="w-3.5 h-3.5" /> Line
                  </button>
                </div>
              </div>
              {addEventOpen && (
                <div
                  className="rounded-md border border-rose-200 bg-rose-50/50 p-3 space-y-2"
                  data-testid="proposal-add-event-panel"
                >
                  <div className="text-[11px] uppercase tracking-widest text-rose-800 font-semibold">
                    New Event / Day
                  </div>
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-12 sm:col-span-5">
                      <label className="label text-[10px] uppercase tracking-widest text-stone-500">
                        Event name
                      </label>
                      <input
                        className="input"
                        list="proposal-add-event-name-options"
                        placeholder="e.g., Haldi, Mehendi, Reception"
                        value={newEventName}
                        onChange={(e) => setNewEventName(e.target.value)}
                        data-testid="proposal-add-event-name"
                      />
                      <datalist id="proposal-add-event-name-options">
                        {["Haldi", "Mehendi", "Sangeet", "Cocktail", "Engagement", "Ring Ceremony", "Roka", "Tilak", "Reception", "Wedding", "Pheras", "Baraat", "Pre-Wedding"].map((n) => (
                          <option key={n} value={n} />
                        ))}
                      </datalist>
                    </div>
                    <div className="col-span-8 sm:col-span-4">
                      <label className="label text-[10px] uppercase tracking-widest text-stone-500">
                        Event date
                      </label>
                      <input
                        type="date"
                        className="input"
                        value={newEventDate}
                        onChange={(e) => setNewEventDate(e.target.value)}
                        data-testid="proposal-add-event-date"
                      />
                    </div>
                    <div className="col-span-4 sm:col-span-3 flex gap-2">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm flex-1"
                        disabled={!newEventDate || !newEventName.trim()}
                        onClick={() => {
                          const d = (newEventDate || "").trim();
                          const n = (newEventName || "").trim();
                          if (!d || !n) return;
                          // Avoid duplicates for the same date — update the
                          // name instead of stacking multiple entries.
                          setExtraEvents((prev) => {
                            const idx = prev.findIndex((ev) => (ev.event_date || "").trim() === d);
                            if (idx >= 0) {
                              const next = [...prev];
                              next[idx] = { event_date: d, event_name: n };
                              return next;
                            }
                            return [...prev, { event_date: d, event_name: n }];
                          });
                          // Seed one blank line item under this new event
                          // so the section appears immediately and the
                          // studio can start typing services right away.
                          setLines((ls) => [
                            ...ls,
                            { label: "", quantity: 1, rate: 0, event_date: d, is_complimentary: false },
                          ]);
                          setAddEventOpen(false);
                          setNewEventName("");
                          setNewEventDate("");
                        }}
                        data-testid="proposal-add-event-confirm"
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          setAddEventOpen(false);
                          setNewEventName("");
                          setNewEventDate("");
                        }}
                        data-testid="proposal-add-event-cancel"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                  <p className="text-[11px] text-stone-500">
                    This adds the event only to this proposal — the source booking stays unchanged.
                  </p>
                </div>
              )}
              <div className="space-y-2">
                {(() => {
                  // iter143 — Group lines by event_date so the editor
                  // mirrors the public-proposal / PDF layout (Day 1 ·
                  // Sangeet · 12 Dec 2027 → services under it). Keeps
                  // the same data model — we only insert visual section
                  // headers above each date break.
                  // iter144 — Also STABLE-SORT lines by event_date so
                  // any move / duplicate action never produces a
                  // duplicated group header, without reshuffling rows
                  // that already sit inside the same group.
                  const rows = [];
                  let lastKey = null;
                  const dayEvents = booking?.day_event_types || {};
                  const dayLookup = {};
                  for (let n = 1; n <= 5; n++) {
                    const d = (booking?.[`day${n}`] || "").trim();
                    if (d) dayLookup[d] = { day_no: n, event: dayEvents[`day${n}`] || booking?.event || "" };
                  }
                  (booking?.extra_days || []).forEach((ed, idx) => {
                    if (ed?.date) dayLookup[ed.date] = { day_no: 6 + idx, event: ed.event_type || booking?.event || "" };
                  });
                  // iter153 — Proposal-scoped extra events added inside
                  // the editor also participate in the group header so
                  // their event name shows above the section.
                  extraEvents.forEach((ev, idx) => {
                    const d = (ev?.event_date || "").trim();
                    if (!d) return;
                    if (!dayLookup[d]) {
                      dayLookup[d] = {
                        day_no: 0,
                        event: ev?.event_name || "Additional Event",
                        added_here: true,
                      };
                    } else if (ev?.event_name) {
                      // Prefer the studio's explicit name over any default.
                      dayLookup[d] = { ...dayLookup[d], event: ev.event_name, added_here: true };
                    }
                  });
                  // Preserve original index for stable data mutations while
                  // rendering in sorted order.
                  const indexed = lines.map((li, i) => ({ li, i }));
                  indexed.sort((a, b) => {
                    const ka = (a.li.event_date || "").trim();
                    const kb = (b.li.event_date || "").trim();
                    // Package (empty date) rows always render last so the
                    // dated event blocks stay grouped up top.
                    if (!ka && kb) return 1;
                    if (ka && !kb) return -1;
                    if (ka === kb) return a.i - b.i;
                    return ka < kb ? -1 : 1;
                  });
                  // iter146 — Render a "close group" subtotal editor row
                  // at the end of every group. Helper to avoid duplication.
                  const pushGroupCloser = (closeKey) => {
                    if (!closeKey) return;
                    const overrideKey = closeKey === "__package__" ? "" : closeKey;
                    const raw = subtotalOverrides?.[overrideKey];
                    const hasOverride = raw !== undefined && raw !== null && String(raw).trim() !== "";
                    const auto = Number(eventAutoSubtotals[overrideKey] || 0);
                    const displayVal = hasOverride ? String(raw) : (auto ? String(auto) : "");
                    rows.push(
                      <div
                        key={`sub-${closeKey}`}
                        className="mt-2 mb-1 pl-3 pr-3 py-2 bg-amber-50/60 border-l-2 border-amber-500 rounded-sm flex items-center gap-3 text-[11px]"
                        data-testid={`proposal-event-subtotal-${closeKey}`}
                      >
                        <span className="uppercase tracking-widest text-amber-800 font-semibold shrink-0">
                          Event Subtotal
                        </span>
                        <span className="text-stone-400 hidden sm:inline">
                          Auto: {inr(auto)}
                        </span>
                        <div className="ml-auto flex items-center gap-1.5">
                          <span className="text-stone-500 font-mono">₹</span>
                          <input
                            type="number"
                            min={0}
                            placeholder={String(auto)}
                            value={hasOverride ? displayVal : ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setSubtotalOverrides((prev) => {
                                const next = { ...(prev || {}) };
                                if (val === "" || val === null) delete next[overrideKey];
                                else next[overrideKey] = val;
                                return next;
                              });
                            }}
                            className="input py-1 text-xs text-right font-mono w-32"
                            data-testid={`proposal-event-subtotal-input-${closeKey}`}
                          />
                          {hasOverride && (
                            <button
                              type="button"
                              onClick={() => setSubtotalOverrides((prev) => {
                                const next = { ...(prev || {}) };
                                delete next[overrideKey];
                                return next;
                              })}
                              className="text-[10px] tracking-widest uppercase text-stone-500 hover:text-rose-700 underline underline-offset-2"
                              title="Reset to auto"
                              data-testid={`proposal-event-subtotal-reset-${closeKey}`}
                            >
                              Reset
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  };
                  indexed.forEach(({ li, i }) => {
                    const key = (li.event_date || "").trim() || "__package__";
                    if (key !== lastKey) {
                      // Close the previous group before opening the new one
                      pushGroupCloser(lastKey);
                      const info = dayLookup[key] || {};
                      const evName = info.event || "";
                      const dayNo = info.day_no || 0;
                      const addedHere = !!info.added_here;
                      rows.push(
                        <div
                          key={`hdr-${key}`}
                          className={`mt-3 first:mt-0 px-2 py-1.5 bg-stone-50 border-l-2 rounded-sm text-[11px] uppercase tracking-widest text-stone-600 flex items-baseline gap-3 ${addedHere ? "border-rose-400" : "border-rose-700"}`}
                          data-testid={`proposal-editor-event-header-${key}`}
                        >
                          <span className="font-semibold text-rose-700">
                            {key === "__package__" ? "Package Inclusions" : (dayNo ? `Day ${dayNo}` : "Event")}
                          </span>
                          {evName && key !== "__package__" && (
                            <span className="text-stone-800 normal-case tracking-normal font-medium">{evName}</span>
                          )}
                          {addedHere && (
                            <span
                              className="normal-case tracking-normal text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 font-medium"
                              data-testid={`proposal-editor-event-added-pill-${key}`}
                            >
                              Added in proposal
                            </span>
                          )}
                          {key !== "__package__" && (
                            <span className="ml-auto text-stone-500 tracking-normal normal-case">{key}</span>
                          )}
                          {addedHere && (
                            <button
                              type="button"
                              onClick={() => {
                                if (typeof window !== "undefined") {
                                  const ok = window.confirm("Remove this added event and all its line items from the proposal?");
                                  if (!ok) return;
                                }
                                setExtraEvents((prev) => prev.filter((ev) => (ev.event_date || "").trim() !== key));
                                setLines((ls) => ls.filter((li) => (li.event_date || "").trim() !== key));
                                setSubtotalOverrides((prev) => {
                                  const next = { ...(prev || {}) };
                                  delete next[key];
                                  return next;
                                });
                              }}
                              className="normal-case tracking-normal text-[10px] text-rose-700 hover:text-rose-900 underline underline-offset-2"
                              title="Remove this added event"
                              data-testid={`proposal-editor-event-remove-${key}`}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      );
                      lastKey = key;
                    }
                    const rowSubtotal = li.is_complimentary
                      ? 0
                      : Number(li.rate || 0) * Number(li.quantity || 1);
                    rows.push(
                  <div key={`li-${i}`} className={`rounded-md ${li.is_complimentary ? "bg-emerald-50/40 border border-emerald-100 px-2 py-1.5" : ""}`} data-testid={`proposal-line-${i}`}>
                    <div className="grid grid-cols-12 gap-2 items-center">
                    <input
                      className="input col-span-5"
                      placeholder="Service description"
                      value={li.label}
                      onChange={(e) => patchLine(i, { label: e.target.value })}
                      data-testid={`proposal-line-label-${i}`}
                    />
                    {bookingDates.length > 0 ? (
                      <select
                        className="input col-span-3 text-xs"
                        value={li.event_date || ""}
                        onChange={(e) => patchLine(i, { event_date: e.target.value })}
                        data-testid={`proposal-line-date-${i}`}
                        title="Event date this line belongs to"
                      >
                        <option value="">— Package —</option>
                        {bookingDates.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="date"
                        className="input col-span-3 text-xs"
                        value={li.event_date || ""}
                        onChange={(e) => patchLine(i, { event_date: e.target.value })}
                        data-testid={`proposal-line-date-${i}`}
                      />
                    )}
                    <input
                      type="number"
                      min={1}
                      className="input col-span-1 text-center"
                      value={li.quantity}
                      onChange={(e) => patchLine(i, { quantity: e.target.value })}
                      data-testid={`proposal-line-qty-${i}`}
                    />
                    <div className="col-span-2 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">₹</span>
                      <input
                        type="number"
                        min={0}
                        className={`input pl-6 text-right ${li.is_complimentary ? "opacity-60" : ""}`}
                        value={li.rate}
                        onChange={(e) => patchLine(i, { rate: e.target.value })}
                        disabled={li.is_complimentary}
                        data-testid={`proposal-line-rate-${i}`}
                      />
                    </div>
                    <div className="col-span-1 flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => patchLine(i, { is_complimentary: !li.is_complimentary })}
                        className={`p-1.5 rounded transition-colors ${li.is_complimentary ? "text-emerald-700 bg-emerald-100" : "text-stone-400 hover:text-emerald-700 hover:bg-emerald-50"}`}
                        title={li.is_complimentary ? "Complimentary (remove flag)" : "Mark as complimentary"}
                        data-testid={`proposal-line-comp-${i}`}
                      >
                        <Gift className="w-3.5 h-3.5" strokeWidth={1.8} />
                      </button>
                      <button
                        type="button"
                        onClick={() => duplicateLine(i)}
                        className="p-1.5 rounded text-stone-400 hover:text-sky-700 hover:bg-sky-50 transition-colors"
                        title="Duplicate line"
                        data-testid={`proposal-line-duplicate-${i}`}
                      >
                        <CopyPlus className="w-3.5 h-3.5" strokeWidth={1.8} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeLine(i)}
                        className="p-1.5 rounded text-stone-400 hover:text-red-700 hover:bg-red-50 transition-colors"
                        title="Remove"
                        data-testid={`proposal-line-remove-${i}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                      </button>
                    </div>
                    </div>
                    <div className="pl-1 pr-1 mt-1 flex items-center justify-between text-[11px] tracking-wide">
                      {li.is_complimentary ? (
                        <span className="text-emerald-700 uppercase tracking-widest text-[10px] font-semibold" data-testid={`proposal-line-comp-badge-${i}`}>
                          Complimentary · included at no cost
                        </span>
                      ) : (
                        <span className="text-stone-400">Line total</span>
                      )}
                      <span className={`font-mono ${li.is_complimentary ? "text-emerald-800" : "text-stone-700"}`} data-testid={`proposal-line-subtotal-${i}`}>
                        {li.is_complimentary ? "FREE" : inr(rowSubtotal)}
                      </span>
                    </div>
                  </div>
                    );
                  });
                  // Close the very last group so its subtotal editor shows too.
                  pushGroupCloser(lastKey);
                  return rows;
                })()}
              </div>

              {services.length > 0 && (
                <div className="pt-2">
                  <div className="editorial-eyebrow mb-2">Quick Add</div>
                  <div className="flex flex-wrap gap-1.5">
                    {services.map((s) => {
                      const already = lines.some((l) => l.label === s.label);
                      return (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => addFromCatalog(s)}
                          disabled={already}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                            already
                              ? "bg-stone-100 text-stone-400 border-stone-200 cursor-not-allowed"
                              : "bg-white text-stone-700 border-stone-300 hover:border-rose-400 hover:text-rose-700"
                          }`}
                          data-testid={`proposal-quickadd-${s.key}`}
                        >
                          {already ? "✓ " : "+ "}
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="label">GST %</label>
                <input
                  type="number"
                  min={0}
                  className="input"
                  value={gstPercent}
                  onChange={(e) => setGstPercent(e.target.value)}
                  placeholder="0"
                  data-testid="proposal-gst"
                />
              </div>
              <div>
                <label className="label">Advance expected (₹)</label>
                <input
                  type="number"
                  min={0}
                  className="input"
                  value={advanceExpected}
                  onChange={(e) => setAdvanceExpected(e.target.value)}
                  placeholder="0"
                  data-testid="proposal-advance"
                />
              </div>
              <div>
                <label className="label">Validity (days)</label>
                <input
                  type="number"
                  min={1}
                  className="input"
                  value={validity}
                  onChange={(e) => setValidity(e.target.value)}
                  data-testid="proposal-validity"
                />
              </div>
            </div>

            <div>
              <label className="label">Client email <span className="text-stone-400 font-normal">(for email share)</span></label>
              <input
                type="email"
                className="input"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="couple@example.com"
                data-testid="proposal-client-email"
              />
            </div>

            <div>
              <label className="label">Notes</label>
              <textarea
                rows={2}
                className="input resize-none"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any package inclusions or terms…"
                data-testid="proposal-notes"
              />
            </div>

            <div className="card p-4 bg-rose-50/40 border-rose-100">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-stone-500">Subtotal</div>
                <div className="text-right font-mono">{inr(subtotal)}</div>
                {Number(gstPercent) > 0 && (
                  <>
                    <div className="text-stone-500">GST ({gstPercent}%)</div>
                    <div className="text-right font-mono">{inr(gstAmt)}</div>
                  </>
                )}
                <div className="font-heading text-rose-800 text-base pt-2 border-t border-rose-200 col-span-1">Grand Total</div>
                <div className="text-right font-heading font-semibold text-rose-800 text-lg pt-2 border-t border-rose-200" data-testid="proposal-grand-total">
                  {inr(total)}
                </div>
                {Number(advanceExpected) > 0 && (
                  <>
                    <div className="text-stone-500">Advance expected</div>
                    <div className="text-right font-mono">{inr(Number(advanceExpected))}</div>
                    <div className="text-stone-500">Balance later</div>
                    <div className="text-right font-mono">{inr(balance)}</div>
                  </>
                )}
              </div>
            </div>

            {err && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="proposal-error">
                {err}
              </div>
            )}
            <div className="flex justify-end gap-2 items-center">
              {hasExistingQuote && !isDirty && (
                <span className="text-[11px] uppercase tracking-widest text-stone-500 mr-1" data-testid="proposal-nothing-changed">
                  Nothing to update
                </span>
              )}
              <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy || (hasExistingQuote && !isDirty)}
                data-testid="proposal-generate"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.6} />}
                {hasExistingQuote ? "Update Proposal" : "Create Proposal"}
              </button>
            </div>
          </form>
        )}

        {step === 2 && result && (
          <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="proposal-preview">
            <div className="card p-4 bg-emerald-50/50 border-emerald-200 flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                <Check className="w-5 h-5 text-emerald-700" strokeWidth={2.4} />
              </div>
              <div className="text-sm text-emerald-900 min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-heading font-semibold text-base">Proposal ready to share</div>
                  {(() => {
                    const s = STATUS_STYLES[publicStatus] || STATUS_STYLES.sent;
                    return (
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${s.cls}`}
                        data-testid="proposal-status-pill"
                      >
                        {s.label}
                      </span>
                    );
                  })()}
                </div>
                <div className="text-emerald-800/80 text-xs mt-1">
                  Grand total {inr(result.quote?.grand_total || 0)} · Valid for {result.quote?.validity_days} days · # {result.quote?.number}
                </div>
              </div>
            </div>

            {/* iter144 — Live analytics card */}
            <div className="card p-3 bg-white border-stone-200" data-testid="proposal-analytics-card">
              <div className="editorial-eyebrow mb-2 flex items-center gap-1.5">
                <Eye className="w-3 h-3" /> Client Engagement
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <div className="text-[10px] tracking-widest uppercase text-stone-500">Views</div>
                  <div className="mt-1 font-heading text-lg text-stone-800" data-testid="proposal-analytics-views">
                    {analytics?.view_count ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] tracking-widest uppercase text-stone-500">Last viewed</div>
                  <div className="mt-1 text-stone-800 text-xs leading-snug" data-testid="proposal-analytics-last-viewed">
                    {analytics?.last_viewed_at_pretty || <span className="text-stone-400">—</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] tracking-widest uppercase text-stone-500 flex items-center gap-1">
                    <Smartphone className="w-3 h-3" /> Device
                  </div>
                  <div className="mt-1 text-stone-800 text-xs leading-snug" data-testid="proposal-analytics-device">
                    {analytics?.last_viewed_device || <span className="text-stone-400">—</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] tracking-widest uppercase text-stone-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Last email
                  </div>
                  <div className="mt-1 text-stone-800 text-xs leading-snug" data-testid="proposal-analytics-last-email">
                    {analytics?.last_email_at_pretty || <span className="text-stone-400">Not sent</span>}
                  </div>
                </div>
              </div>
            </div>

            {publicStatus === "rejected" && (
              <div className="card p-3 bg-rose-50 border-rose-200 flex items-start gap-2" data-testid="proposal-rejected-badge">
                <XCircle className="w-4 h-4 text-rose-700 mt-0.5 shrink-0" strokeWidth={2} />
                <div className="text-sm text-rose-900">
                  <div className="font-semibold">Client politely declined this proposal.</div>
                  <div className="text-rose-700 text-xs mt-0.5">You can still make revisions and send a fresh one.</div>
                </div>
              </div>
            )}

            {publicStatus === "expired" && (
              <div className="card p-3 bg-stone-100 border-stone-200 flex items-start gap-2" data-testid="proposal-expired-badge">
                <AlertTriangle className="w-4 h-4 text-stone-600 mt-0.5 shrink-0" strokeWidth={2} />
                <div className="text-sm text-stone-700">
                  <div className="font-semibold">This proposal's validity window has lapsed.</div>
                  <div className="text-stone-500 text-xs mt-0.5">Generate a new revision to restart the acceptance window.</div>
                </div>
              </div>
            )}

            {acceptedInfo && (
              <div className="card p-3 bg-emerald-50 border-emerald-300 flex items-center gap-2" data-testid="proposal-accepted-badge">
                <CheckCircle2 className="w-4 h-4 text-emerald-700" strokeWidth={2} />
                <div className="text-sm text-emerald-900">
                  <span className="font-semibold">Client accepted the proposal.</span>{" "}
                  <span className="text-emerald-700 text-xs">
                    {acceptedInfo.at ? fmtDate(acceptedInfo.at.slice(0, 10)) : ""}
                  </span>
                </div>
              </div>
            )}

            {/* Beautiful proposal page link */}
            <div className="card p-3 flex flex-wrap items-center gap-2 bg-amber-50/60 border-amber-200">
              <div className="min-w-0 flex-1">
                <div className="editorial-eyebrow flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> Proposal Page
                </div>
                <div className="text-xs font-mono text-stone-600 truncate" title={proposalPageUrl}>
                  {proposalPageUrl}
                </div>
              </div>
              <button type="button" onClick={() => copyLink("page")} className="btn btn-outline btn-sm" data-testid="proposal-copy-page">
                {copied.page ? <Check className="w-3.5 h-3.5" strokeWidth={2.4} /> : <Copy className="w-3.5 h-3.5" />}
                {copied.page ? "Copied" : "Copy"}
              </button>
              <a href={proposalPageUrl} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm" data-testid="proposal-open-page">
                <ExternalLink className="w-3.5 h-3.5" /> Open
              </a>
            </div>

            {/* iter176 — Per-proposal content customization (Deliverables + T&Cs) */}
            {proposalCaps.canEdit && (
              <div className="card p-3 flex flex-wrap items-center gap-2 bg-stone-50 border-stone-200" data-testid="proposal-content-customize-card">
                <div className="min-w-0 flex-1">
                  <div className="editorial-eyebrow flex items-center gap-1.5">
                    <Pencil className="w-3 h-3" /> Deliverables &amp; Terms
                  </div>
                  <div className="text-xs text-stone-600">
                    Customize the Deliverables sections and Terms for this proposal only. Global defaults stay untouched.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setContentEditorOpen(true)}
                  className="btn btn-outline btn-sm"
                  data-testid="proposal-customize-content-btn"
                >
                  <Pencil className="w-3.5 h-3.5" /> Customize for this Proposal
                </button>
              </div>
            )}

            {/* PDF preview iframe */}
            <div className="card p-0 overflow-hidden" style={{ height: 480 }}>
              <iframe
                src={fullPdfUrl}
                title="Proposal preview"
                className="w-full h-full border-0"
                data-testid="proposal-pdf-iframe"
              />
            </div>

            {/* PDF actions */}
            <div className="card p-3 flex flex-wrap items-center gap-2 bg-stone-50 border-stone-200">
              <div className="min-w-0 flex-1">
                <div className="editorial-eyebrow flex items-center gap-1.5">
                  <FileText className="w-3 h-3" /> Printable PDF
                </div>
                <div className="text-xs font-mono text-stone-600 truncate" title={fullPdfUrl}>
                  {fullPdfUrl}
                </div>
              </div>
              <button type="button" onClick={() => copyLink("pdf")} className="btn btn-outline btn-sm" data-testid="proposal-copy-pdf">
                {copied.pdf ? <Check className="w-3.5 h-3.5" strokeWidth={2.4} /> : <Copy className="w-3.5 h-3.5" />}
                {copied.pdf ? "Copied" : "Copy"}
              </button>
              <a href={fullPdfUrl} download={`Kahani-Proposal-${(booking.couple_name || booking.client_name || "Client").replace(/[^A-Za-z0-9]+/g, "-")}.pdf`} className="btn btn-outline btn-sm" data-testid="proposal-download">
                <Download className="w-3.5 h-3.5" /> Download
              </a>
            </div>

            {/* Email share */}
            <div className="card p-4 bg-white border-stone-200 space-y-3">
              <div className="editorial-eyebrow flex items-center gap-1.5">
                <Mail className="w-3 h-3" /> Email Share
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-start">
                <input
                  type="email"
                  className="input"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="couple@example.com"
                  data-testid="proposal-email-recipient"
                />
                <button
                  type="button"
                  onClick={sendEmail}
                  className="btn btn-outline btn-sm"
                  disabled={emailBusy || !clientEmail?.trim()}
                  data-testid="proposal-send-email"
                >
                  {emailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Send email
                </button>
              </div>
              <textarea
                rows={2}
                className="input resize-none text-xs"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Optional intro paragraph the client will see above the proposal link…"
                data-testid="proposal-email-message"
              />
              <p className="text-[10px] text-stone-500">
                Sends through Resend using the studio's verified sender. Recipient will get a branded email with the proposal link + PDF.
              </p>
            </div>

            <div className="flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="btn btn-outline btn-sm"
                data-testid="proposal-back"
              >
                Edit line items
              </button>
              {waHref && (
                <a
                  href={waHref}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-primary btn-sm"
                  data-testid="proposal-share-whatsapp"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> Share on WhatsApp
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* iter176 — Per-proposal content editor drawer */}
      {contentEditorOpen && booking?.id && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto"
          onClick={() => setContentEditorOpen(false)}
          data-testid="proposal-content-editor-overlay"
        >
          <div
            className="bg-white w-full max-w-5xl my-8 mx-4 rounded-xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200">
              <div>
                <div className="editorial-eyebrow">Proposal · {booking.couple_name || booking.client_name || "Client"}</div>
                <div className="font-heading font-semibold text-lg">Customize Deliverables &amp; Terms</div>
              </div>
              <button
                type="button"
                className="p-2 rounded hover:bg-stone-100"
                onClick={() => setContentEditorOpen(false)}
                data-testid="proposal-content-editor-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <ProposalContentEditor
                mode="proposal"
                bookingId={booking.id}
                onClose={() => setContentEditorOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
